import { useState, useCallback } from "react";
import { InteractionType } from "@azure/msal-browser";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import axios from "axios";

import { StorageKeys } from "../lib/storageKeys";

interface MsalRequest {
  scopes?: string[];
}

const useFetchWithMsal = (msalRequest: MsalRequest) => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [data, setData] = useState<unknown>(null);
  const account = instance.getActiveAccount();

  useMsalAuthentication(InteractionType.Redirect, {
    ...msalRequest,
    account: account ?? undefined,
    redirectUri: "/",
  });

  const getAccessToken = useCallback(async () => {
    if (account && msalRequest?.scopes?.length) {
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          account,
          scopes: msalRequest.scopes,
        });
        if (accessToken) {
          localStorage.setItem(StorageKeys.BackendAccessToken, accessToken);
          return accessToken;
        }
      } catch {
        localStorage.removeItem(StorageKeys.BackendAccessToken);
      }
    }
    return localStorage.getItem(StorageKeys.BackendAccessToken);
  }, [instance, account, msalRequest?.scopes]);

  const execute = useCallback(
    async (
      method: string,
      endpoint: string,
      body: object | null = null,
      headers: Record<string, string | null | undefined> | null = null,
    ) => {
      setError(null);
      setIsLoading(true);

      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("No token available. Please sign in again.");
        }

        const { data: responseData } = await axios(endpoint, {
          method,
          headers: { Authorization: `Bearer ${token}`, ...headers },
          data: body ?? null,
        });

        setData(responseData);
        return responseData;
      } catch (e: unknown) {
        if (
          typeof e === "object" &&
          e !== null &&
          "response" in e &&
          typeof (e as { response?: { status?: number } }).response?.status ===
            "number" &&
          (e as { response: { status: number } }).response.status === 401
        ) {
          localStorage.removeItem(StorageKeys.BackendAccessToken);
        }
        setError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [getAccessToken],
  );

  return { isLoading, error, data, account, execute };
};

export default useFetchWithMsal;
